import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AuthModal } from "@/components/AuthModal";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { Check, Crown, Loader2, ShieldCheck, Smartphone, Building2 } from "lucide-react";

const FREE = ["Browse verified listings", "Interactive map search", "WhatsApp the Dotlands desk", "Post up to 10 free ads"];
const PRO = ["Everything in Free", "Direct WhatsApp to property owners", "Instant owner phone access", "Verified buyer badge", "Zero brokerage direct deals", "Unlimited listings"];
const DEV = ["Everything in Premium", "Bulk listing import", "Auto-featured premium listings", "Priority placement & top ranking", "Builder / agent profile", "Dedicated account manager"];

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export default function Premium() {
  const { user, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [paying, setPaying] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("premium");

  useEffect(() => { api.get("/payments/config").then(({ data }) => setConfig(data)); }, []);

  const plans = config?.plans || {};
  const price = (p) => plans[p]?.price ?? (p === "developer" ? 9999 : 999);
  const upi = config?.owner_upi;
  const upiLink = upi ? `upi://pay?pa=${upi}&pn=Dotlands%20Realty&am=${price(selectedPlan)}&cu=INR&tn=${encodeURIComponent("Dotlands " + (selectedPlan === "developer" ? "Developer" : "Premium"))}` : null;
  const qrSrc = upiLink ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(upiLink)}` : null;

  const startUpgrade = async (plan) => {
    if (!user) return setAuthOpen(true);
    setSelectedPlan(plan);
    if (config?.demo) { setPayOpen(true); return; }
    await pay(plan);
  };

  const pay = async (planArg) => {
    const plan = planArg || selectedPlan;
    setPaying(true);
    try {
      const { data: order } = await api.post("/payments/create-order", { plan });
      if (order.demo) {
        await new Promise((r) => setTimeout(r, 1500));
        const { data } = await api.post("/payments/verify", { order_id: order.order_id, payment_id: "upi_demo", signature: "upi_demo" });
        setUser(data.user);
        toast.success(`Payment received — ${plan === "developer" ? "Developer" : "Premium"} plan active! 🎉`);
        setPayOpen(false);
        await refresh();
      } else {
        await loadRazorpay();
        const rzp = new window.Razorpay({
          key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
          name: "Dotlands.in", description: plans[plan]?.label || "Premium",
          handler: async (res) => {
            const { data } = await api.post("/payments/verify", { order_id: res.razorpay_order_id, payment_id: res.razorpay_payment_id, signature: res.razorpay_signature });
            setUser(data.user); toast.success("Plan activated!"); setPayOpen(false); await refresh();
          },
          prefill: { name: user.name, contact: user.phone }, theme: { color: "#0E3B2E" },
        });
        rzp.open();
      }
    } catch (e) { toast.error(apiError(e)); } finally { setPaying(false); }
  };

  const currentPlan = user?.plan || "free";

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto py-12">
      <Seo
        title="Premium & Developer Plans | Dotlands Realty"
        description="Upgrade to connect directly with property owners, post unlimited listings and get auto-featured placement. Premium and Developer plans for buyers, builders and agents in Telangana."
        path="/premium"
      />
      <div className="text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C8A24C]/15 text-[#A8863A] text-xs font-bold uppercase tracking-wide"><Crown className="w-3.5 h-3.5" /> Plans</span>
        <h1 className="text-3xl sm:text-5xl font-heading font-extrabold text-[#0E3B2E] mt-4">Choose your plan</h1>
        <p className="text-slate-500 mt-3 max-w-2xl mx-auto">Go Premium to connect directly with owners, or pick the Developer plan for bulk uploads and auto-featured premium listings.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-10 items-start">
        <PlanCard title="Standard" price="Free" subtitle="For browsing & posting" features={FREE} current={currentPlan === "free"}
          cta={<Button variant="outline" className="w-full rounded-full" disabled>{currentPlan === "free" ? "Current plan" : "Free"}</Button>} />

        <PlanCard dark popular title="Premium Pro" icon={<Crown className="w-5 h-5 text-[#C8A24C]" />} price={inr(price("premium"))} period="/year" subtitle="For serious buyers" features={PRO} current={currentPlan === "premium"}
          cta={<Button data-testid="upgrade-premium-btn" onClick={() => startUpgrade("premium")} disabled={currentPlan !== "free"} className="w-full bg-[#C8A24C] hover:bg-[#A8863A] text-[#0E3B2E] font-bold rounded-full h-12">{currentPlan === "premium" ? "Active ✓" : currentPlan === "developer" ? "Included" : "Get Premium"}</Button>}
          footer={<PayNote />} />

        <PlanCard title="Developer" icon={<Building2 className="w-5 h-5 text-[#0E3B2E]" />} price={inr(price("developer"))} period="/year" subtitle="For builders & agents" features={DEV} current={currentPlan === "developer"} highlight
          cta={<Button data-testid="upgrade-developer-btn" onClick={() => startUpgrade("developer")} disabled={currentPlan === "developer"} className="w-full bg-[#0E3B2E] hover:bg-[#0A2A20] rounded-full h-12 font-bold">{currentPlan === "developer" ? "Active ✓" : "Get Developer"}</Button>}
          footer={<PayNote />} />
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="sm:max-w-sm rounded-2xl" data-testid="payment-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <span className="px-2 py-1 rounded bg-[#5F259F] text-white text-sm font-bold">PhonePe</span> Pay {inr(price(selectedPlan))}
            </DialogTitle>
          </DialogHeader>
          {config?.demo && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">DEMO activation — scan to pay via UPI, then tap the button to activate instantly.</div>}
          <div className="text-center text-sm text-slate-500">{selectedPlan === "developer" ? "Developer Plan" : "Premium Pro"} · 1 year</div>
          <div className="flex flex-col items-center py-3">
            {qrSrc ? <img src={qrSrc} alt="UPI QR" className="w-44 h-44 rounded-xl border border-slate-200" data-testid="upi-qr" />
              : <div className="w-44 h-44 rounded-xl bg-slate-100 border animate-pulse" />}
            <p className="text-sm text-slate-500 mt-3 flex items-center gap-1.5"><Smartphone className="w-4 h-4" /> Scan with PhonePe / any UPI app</p>
            {upi && <p className="text-xs text-slate-400 mt-1">Credited to Dotlands · UPI <span className="font-semibold text-[#0E3B2E]">{upi}</span></p>}
          </div>
          {upiLink && <a href={upiLink} className="w-full flex items-center justify-center gap-2 border border-[#5F259F] text-[#5F259F] hover:bg-[#5F259F]/5 rounded-lg h-11 text-sm font-semibold mb-2">Open UPI App to Pay</a>}
          <Button data-testid="confirm-payment-btn" onClick={pay} disabled={paying} className="w-full bg-[#0E3B2E] hover:bg-[#0A2A20] h-11">
            {paying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Confirming...</> : `I've Paid — Activate ${selectedPlan === "developer" ? "Developer" : "Premium"}`}
          </Button>
          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="w-3.5 h-3.5" /> Payments go directly to Dotlands PhonePe</div>
        </DialogContent>
      </Dialog>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}

function PlanCard({ title, price, period, subtitle, features, cta, dark, popular, icon, current, highlight, footer }) {
  return (
    <div className={`relative rounded-3xl p-8 ${dark ? "bg-gradient-to-br from-[#0E3B2E] to-[#0A2A20] text-white shadow-2xl" : `bg-white border ${highlight ? "border-[#C8A24C] ring-1 ring-[#C8A24C]/30" : "border-slate-200"}`}`}>
      {current ? <span className="absolute top-6 right-6 px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold">CURRENT</span>
        : popular && <span className="absolute top-6 right-6 px-3 py-1 rounded-full bg-[#C8A24C] text-[#0E3B2E] text-xs font-bold">POPULAR</span>}
      <h3 className={`font-heading font-bold text-xl flex items-center gap-2 ${dark ? "" : "text-[#0E3B2E]"}`}>{icon} {title}</h3>
      <div className={`text-4xl font-heading font-extrabold mt-3 ${dark ? "" : "text-slate-900"}`}>{price}{period && <span className="text-base font-medium text-slate-400">{period}</span>}</div>
      <p className={`text-sm mt-1 ${dark ? "text-slate-300" : "text-slate-500"}`}>{subtitle}</p>
      <ul className="mt-6 space-y-3">
        {features.map((x) => <li key={x} className={`flex items-start gap-2 text-sm ${dark ? "" : "text-slate-700"}`}><Check className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-[#C8A24C]" : "text-emerald-600"}`} /> {x}</li>)}
      </ul>
      <div className="mt-8">{cta}</div>
      {footer}
    </div>
  );
}

function PayNote() {
  return (
    <div className="flex items-center justify-center gap-2 mt-3 text-xs text-slate-400">
      <span className="px-2 py-1 rounded bg-[#5F259F] text-white font-semibold">PhonePe</span>
      <span>UPI · Cards · Netbanking</span>
    </div>
  );
}
